import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { shouldSubmitSearch } from '../searchSubmit';
import { HeaderMoreMenu } from './HeaderMoreMenu';
import { LogoMark } from './LogoMark';
import { SearchField } from './SearchField';
import { SiteFooter } from './SiteFooter';
import { PageHead } from './PageHead';
import { FacebookFollowLink } from './FacebookFollowLink';
import './SearchResultsPage.css';
import './AboutPage.css';

export function AboutPage() {
  const navigate = useNavigate();
  const [draftQuery, setDraftQuery] = useState('');

  function applySearch(query) {
    const trimmed = typeof query === 'string' ? query.trim() : '';
    if (!shouldSubmitSearch(trimmed)) {
      return;
    }
    navigate(`/search?q=${encodeURIComponent(trimmed)}`);
  }

  return (
    <div className="results-page">
      <PageHead
        title="About Shops.mt — Malta online store search"
        description="Shops.mt is Malta's product search engine. Compare products from local online stores, support Maltese retailers, and find what you need in one place."
        canonicalPath="/about"
      />
      <header className="results-page__header">
        <div className="results-page__header-inner">
          <div className="results-page__header-left">
            <LogoMark variant="header" />
            <HeaderMoreMenu />
          </div>
          <div className="results-page__header-center">
            <SearchField
              id="about-search"
              className="results-page__search search-bar"
              value={draftQuery}
              onChange={setDraftQuery}
              onSubmitSearch={applySearch}
              placeholder="Search products…"
              gaSearchLocation="about"
            />
          </div>
          <nav
            className="results-page__header-nav"
            aria-label="Site sections"
          >
            <Link
              className="results-page__nav-link results-page__nav-link--current"
              to="/about"
              aria-current="page"
            >
              About
            </Link>
            <Link className="results-page__nav-link" to="/add-store">
              Add your store
            </Link>
          </nav>
        </div>
      </header>

      <div className="about-page">
        <section className="about-page__section about-page__intro" aria-labelledby="about-heading">
          <h1 id="about-heading" className="about-page__title">
            Malta&apos;s shops, one search away
          </h1>
          <div className="about-page__prose">
            <p>
              <strong>Shops.mt</strong> is a dedicated search engine for online
              stores based in Malta. We aggregate products from local retailers
              so shoppers can discover what is in stock, compare options, and
              click through to buy from the merchant they choose — without
              hopping between dozens of tabs.
            </p>
            <p>
              We believe the best version of e‑commerce in Malta is one where
              people default to <strong>shopping locally</strong>: faster
              delivery, clearer returns, human support, and money that stays in
              the community. When consumers choose Maltese businesses first,
              everyone wins.
            </p>
            <p>
              <strong>For shoppers,</strong> Shops.mt saves time, surfaces real
              inventory and prices side by side, and makes it easier to support
              the brands you already love — plus the ones you have not met yet.
            </p>
            <p>
              <strong>For business owners,</strong> listing means qualified
              traffic from people who are already in a buying mindset: they are
              searching with intent, not scrolling aimlessly. You keep the sale
              on your own checkout; we help you get found in the moments that
              matter.{' '}
              <Link to="/add-store">Add your store</Link>.
            </p>
            <p className="about-page__cta-line">
              Whether you are hunting for the right product or growing a store
              with pride in Malta, Shops.mt is built for you.
            </p>
          </div>
        </section>

        <section
          className="about-page__section about-page__facebook"
          aria-labelledby="about-facebook-heading"
        >
          <h2 id="about-facebook-heading" className="about-page__subtitle">
            Join Malta shoppers who compare before they buy
          </h2>
          <p className="about-page__prose about-page__facebook-lede">
            Follow us on Facebook for Malta deals, new stores, and price drops
            as we find them.
          </p>
          <FacebookFollowLink
            variant="button"
            location="about"
            className="about-page__facebook-btn"
          />
        </section>
      </div>
      <SiteFooter />
    </div>
  );
}
